import React, { useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/**
 * Kebab (⋮) menu for table row actions.
 *
 * Owns its own anchor state, so pages just hand it a list of actions:
 *
 *   <RowActionsMenu
 *     actions={[
 *       { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(row) },
 *       { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(row.id), color: 'error' },
 *     ]}
 *   />
 *
 * Falsy entries are dropped, so conditional actions can be written as
 * `canDoThing && { key: 'thing', ... }`.
 */
function RowActionsMenu({ actions, ariaLabel = 'Row actions' }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const items = (actions ?? []).filter(Boolean);
  if (items.length === 0) return null;

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (action) => () => {
    handleClose();
    action.onClick();
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={ariaLabel}
        onClick={handleOpen}
        sx={{ minWidth: 28, minHeight: 28 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {items.map((action) => {
          const item = (
            <MenuItem
              key={action.key ?? action.label}
              onClick={handleSelect(action)}
              disabled={action.disabled}
              sx={action.color ? { color: `${action.color}.main` } : undefined}
            >
              {action.icon && (
                <ListItemIcon sx={{ minWidth: 0, mr: 1, color: 'inherit' }}>{action.icon}</ListItemIcon>
              )}
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{action.label}</ListItemText>
            </MenuItem>
          );

          // A disabled MenuItem swallows pointer events, so a tooltip on it never
          // fires — wrap it so the explanation is still reachable.
          if (action.title) {
            return (
              <Tooltip
                key={action.key ?? action.label}
                title={action.title}
                placement="right"
                disableInteractive
              >
                <span>{item}</span>
              </Tooltip>
            );
          }
          return item;
        })}
      </Menu>
    </>
  );
}

export default RowActionsMenu;
